// frontend/src/hooks/useIdleLogout.js
// Spec: "Auto logout 3 hours."
import { useEffect, useRef } from 'react';

const DEFAULT_MS = Number(process.env.REACT_APP_IDLE_LOGOUT_MS) || 3 * 60 * 60 * 1000;
const STORAGE_KEY = 'domino:lastActivity';
const ACTIVITY_EVENTS = ['mousedown', 'keydown', 'scroll', 'touchstart', 'click'];

/**
 * Logs the user out after `idleMs` without interaction.
 *
 * Activity is stamped into localStorage so the countdown survives a reload and
 * is shared across tabs -- otherwise opening a second tab would reset the timer
 * for a user who has actually walked away.
 */
export default function useIdleLogout(onTimeout, idleMs = DEFAULT_MS, enabled = true) {
    const cbRef = useRef(onTimeout);
    useEffect(() => { cbRef.current = onTimeout; }, [onTimeout]);

    useEffect(() => {
        if (!enabled) return undefined;

        const stamp = () => {
            try {
                localStorage.setItem(STORAGE_KEY, String(Date.now()));
            } catch {
                /* private mode - fall back to in-memory timing only */
            }
        };

        const lastActivity = () => {
            const stored = Number(localStorage.getItem(STORAGE_KEY));
            return Number.isFinite(stored) && stored > 0 ? stored : Date.now();
        };

        // A session resumed after a long absence should end immediately.
        if (Date.now() - lastActivity() >= idleMs) {
            cbRef.current();
            return undefined;
        }
        stamp();

        // Throttle the writes; one per 10s of activity is plenty.
        let lastWrite = Date.now();
        const onActivity = () => {
            if (Date.now() - lastWrite > 10_000) {
                lastWrite = Date.now();
                stamp();
            }
        };
        ACTIVITY_EVENTS.forEach(e => window.addEventListener(e, onActivity, { passive: true }));

        const id = setInterval(() => {
            if (Date.now() - lastActivity() >= idleMs) {
                cbRef.current();
            }
        }, 30_000);

        return () => {
            clearInterval(id);
            ACTIVITY_EVENTS.forEach(e => window.removeEventListener(e, onActivity));
        };
    }, [idleMs, enabled]);
}

export { STORAGE_KEY as IDLE_STORAGE_KEY };
